import { create } from "zustand";

/**
 * What the app-bar connection dot shows. Priority, first match wins:
 *
 *  - `offline`     the browser itself says there is no network
 *                  (`navigator.onLine` / the `offline` event);
 *  - `unreachable` the browser is online but the LAST request that could
 *                  have reached the server got no answer at all
 *                  (`ERR_NETWORK` / timeout — see `client.ts`);
 *  - `busy`        at least one non-safe request (anything but GET / HEAD /
 *                  OPTIONS) is in flight. This app reads with POST too
 *                  (paginated guest list, counts), so the copy says
 *                  "syncing", never "saving";
 *  - `live`        none of the above — nothing has failed since the last
 *                  answer. Honest wording: green is "no failure observed",
 *                  which is why `ConnectionWatcher` keeps a slow heartbeat.
 *
 * Every HTTP answer — 401, 403, 500, 503 included — counts as "reachable".
 * Gateway / Cloudflare error pages arrive cross-origin without CORS headers
 * and therefore surface as `ERR_NETWORK` anyway; an app-emitted 503 (the
 * activity feed on a query timeout) is a server that is very much there.
 */
export type ConnectionStatus = "live" | "busy" | "offline" | "unreachable";

export interface ConnectionState {
  /** Mirror of `navigator.onLine`, kept fresh by the `online`/`offline` events. */
  browserOnline: boolean;
  /** Verdict of the last request that reached (or failed to reach) the API. */
  reachable: boolean;
  /** Non-safe requests currently in flight. Never below zero. */
  inflightWrites: number;
  setBrowserOnline: (online: boolean) => void;
  noteServerAnswered: () => void;
  noteNetworkFailure: () => void;
  writeStarted: () => void;
  writeFinished: () => void;
}

/**
 * Epoch ms of the last server answer, or null before the first one.
 *
 * Deliberately NOT a store field: it changes on every response, so as a
 * field it would make every `set` a real change and re-run every subscriber
 * selector per request. Only the dot's tooltip reads it, at render time.
 */
let lastAnswered: number | null = null;

export function lastAnsweredAt(): number | null {
  return lastAnswered;
}

/** Test seam — the module-level clock has no other reset path. */
export function __resetLastAnsweredForTests(value: number | null = null): void {
  lastAnswered = value;
}

export function deriveStatus(
  s: Pick<ConnectionState, "browserOnline" | "reachable" | "inflightWrites">,
): ConnectionStatus {
  if (!s.browserOnline) return "offline";
  if (!s.reachable) return "unreachable";
  if (s.inflightWrites > 0) return "busy";
  return "live";
}

function initialBrowserOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return typeof navigator.onLine === "boolean" ? navigator.onLine : true;
}

/**
 * Every action returns the SAME state object when nothing changes, so zustand
 * skips the notification (`Object.is` short-circuit) — a burst of identical
 * verdicts costs no render.
 */
export const useConnectionStore = create<ConnectionState>((set) => ({
  browserOnline: initialBrowserOnline(),
  reachable: true,
  inflightWrites: 0,
  setBrowserOnline: (online) =>
    set((s) => (s.browserOnline === online ? s : { browserOnline: online })),
  noteServerAnswered: () => {
    lastAnswered = Date.now();
    // Evidence beats the flag: a server that just answered proves the browser
    // is online even when `navigator.onLine` wrongly says otherwise (virtual
    // adapters, some VPNs) — otherwise that red would only clear on an
    // `online` event that never comes.
    set((s) =>
      s.reachable && s.browserOnline ? s : { reachable: true, browserOnline: true },
    );
  },
  noteNetworkFailure: () => set((s) => (s.reachable ? { reachable: false } : s)),
  writeStarted: () => set((s) => ({ inflightWrites: s.inflightWrites + 1 })),
  writeFinished: () =>
    set((s) => (s.inflightWrites <= 0 ? s : { inflightWrites: s.inflightWrites - 1 })),
}));
